import { LoadingOverlay, Stack } from '@mantine/core';
import { t } from 'i18next';
import { Fragment, useMemo, useRef } from 'react';
import type { CostByInitiativeResponse } from '../../../../api/financials-client/financials-client.type';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { formatFloraCostByInitiativeData } from '../../../../components/cost-by-initiative/cost-by-initiative.helpers';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { setCostByInitiativeChartType } from '../../../../store/financials-store/financials-store.actions';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { secondaryBase } from '../../../../styles/design-tokens';
import { FloraButton } from '../../../flora/flora-button';
import { ChartDisplayTypeButtonSet } from '../shared/chart-display-type-button-set';
import { Chart as BarChart } from './bar-chart';
import {
  filterCostByInitiativeData,
  getInitiativeInfo,
  transformCostByInitiativeData,
} from './cost-by-initiative.helpers';
import { Chart as LineChart } from './line-chart';

interface ChartProps {
  data?: CostByInitiativeResponse[];
  isPending: boolean;
}

export function Chart({ data, isPending }: ChartProps) {
  const selectedChartType = useFinancialsStore((state) => state.filters.costByInitiative.selectedChartType);
  const selectedInitiativeIds = useFinancialsStore((state) => state.filters.costByInitiative.selectedInitiativeIds);

  const chartRef = useRef<HTMLDivElement>(null!);

  const filteredData = filterCostByInitiativeData(data, selectedInitiativeIds);
  const chartData = transformCostByInitiativeData(filteredData);

  const initiativeInfo = useMemo(() => getInitiativeInfo(filteredData), [filteredData]);

  const chart = useMemo(() => {
    if (selectedChartType === ChartType.Line) {
      return <LineChart chartData={chartData} initiativeInfo={initiativeInfo} chartRef={chartRef} />;
    }
    return <BarChart chartData={chartData} initiativeInfo={initiativeInfo} chartRef={chartRef} />;
  }, [selectedChartType, chartData, initiativeInfo]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isPending} loaderProps={{ color: secondaryBase }} />
      {!isPending && Object.keys(data || {}).length === 0 ? (
        <ContentPlaceholder icon="event_busy" text={t('common.labels.no_filtered_data')} />
      ) : (
        <Fragment>
          <ChartDisplayTypeButtonSet
            onSetLineChart={() => setCostByInitiativeChartType(ChartType.Line)}
            onSetBarChart={() => setCostByInitiativeChartType(ChartType.Bar)}
            selectedChartType={selectedChartType}
            onExportChart={() => exportChart(chartRef.current, 'cost-by-initiative-chart.png')}
            floraButton={
              <FloraButton externalData={[formatFloraCostByInitiativeData(data || null)]} disabled={!data} />
            }
          />
          {chart}
        </Fragment>
      )}
    </Stack>
  );
}
